import L from "leaflet";

/**
 * Smart Event Hotspot map marker — single 🔥 circle representing N trucks
 * gathered at one venue. Tier (`small`/`medium`/`major`) scales size +
 * glow intensity so a 12-truck festival visually dwarfs a 3-truck meetup.
 *
 * Implemented as a Leaflet `DivIcon` (no extra image asset; pure CSS) so
 * it animates on the GPU and matches the existing truck-pin design language.
 *
 * `animate=false` returns a static circle — used when the
 * `enable_hotspot_animations` flag is OFF (battery-saver / low-end devices).
 */
const _HOTSPOT_ICON_CACHE = new Map();

export function hotspotIcon(tier = "small", count = 3, animate = true) {
  const key = `${tier}|${count}|${animate ? "a" : "s"}`;
  const cached = _HOTSPOT_ICON_CACHE.get(key);
  if (cached) return cached;

  // Size grows with tier for instant "this is bigger" recognition.
  const sizes = { small: 56, medium: 68, major: 84 };
  const dim = sizes[tier] || 56;
  // Font sizing is scaled so the flame + count combo fills ~60% of the
  // pin's width — empty negative space made the marker read as a generic
  // dot at first glance.
  const fontSize = tier === "major" ? 26 : tier === "medium" ? 22 : 18;
  const flameSize = tier === "major" ? 28 : tier === "medium" ? 24 : 20;
  const pulseClass = animate ? "tt-hotspot-pulse" : "";

  const html = `
    <div class="tt-hotspot tt-hotspot-${tier}" style="width:${dim}px;height:${dim}px;">
      <span class="${pulseClass}" aria-hidden="true"></span>
      <span class="tt-hotspot-core">
        <span class="tt-hotspot-flame" aria-hidden="true" style="font-size:${flameSize}px;">🔥</span>
        <span class="tt-hotspot-count" style="font-size:${fontSize}px;">${count}</span>
      </span>
    </div>
  `;

  const icon = L.divIcon({
    className: "tt-hotspot-icon",
    html,
    iconSize: [dim, dim],
    iconAnchor: [dim / 2, dim / 2],
  });
  _HOTSPOT_ICON_CACHE.set(key, icon);
  return icon;
}
